import React from 'react';
import {
  Alert,
  Box,
  Button,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { TableErrorStateProps } from '../../types';

export const TableErrorState = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  error,
  onRetry,
  columns,
}: TableErrorStateProps<T>) => {
  const theme = useTheme();
  const visibleColumns = columns.filter((col) => !col.hidden);
  const colSpan = visibleColumns.length;

  const renderErrorContent = () => {
    // If error is a React node, render it directly
    if (React.isValidElement(error)) {
      return error;
    }

    // If error is an Error object, extract the message
    const errorMessage = error instanceof Error ? error.message : String(error);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 4,
          px: 2,
          minHeight: 200,
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: theme.palette.error.main,
            mb: 1,
          }}
        >
          <SystemIcon
            iconName="Warning"
            style={{
              fontSize: '24px',
              color: theme.palette.error.main,
            }}
          />
          <Typography
            variant="h6"
            color="error"
            sx={{
              fontFamily: 'General Sans',
              fontWeight: 500,
            }}
          >
            Something went wrong
          </Typography>
        </Box>

        <Alert
          severity="error"
          sx={{
            maxWidth: 400,
            width: '100%',
            '& .MuiAlert-message': {
              fontFamily: 'General Sans',
              fontSize: '14px',
              lineHeight: '150%',
            },
          }}
        >
          {errorMessage ||
            'An unexpected error occurred while loading the data.'}
        </Alert>

        {onRetry && (
          <Button
            variant="outlined"
            color="primary"
            onClick={onRetry}
            startIcon={
              <SystemIcon iconName="ArrowRight" style={{ fontSize: '16px' }} />
            }
            sx={{
              fontFamily: 'General Sans',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: 1,
              px: 3,
              py: 1,
            }}
          >
            Try Again
          </Button>
        )}
      </Box>
    );
  };

  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        sx={{
          border: 'none',
          padding: 0,
        }}
      >
        {renderErrorContent()}
      </TableCell>
    </TableRow>
  );
};
